import { Component, ElementRef, ViewChild, computed, inject, signal } from '@angular/core';
import type { MenuItem } from 'primeng/api';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { Menu, MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { UserService, ApplicationConfigService } from '@cadriciel/core';
import { LayoutService } from '../service/layout.service';
import { AuthService } from '@shared/auth/auth.service';
import { ActiveRoleService } from '@shared/auth/active-role.service';

/**
 * Barre du haut — grammaire Prodicee (docs/redesign/GRAMMAIRE-VISUELLE.md) :
 * bouton menu, logo + nom, puis à droite la bascule d'apparence (bouton rond)
 * et le profil (avatar, nom, rôle actif, chevron).
 *
 * Le menu de profil est un `p-menu` PrimeNG en popup : rôle ARIA « menu »,
 * flèches haut/bas, Échap, retour du focus sur le bouton. Il porte le
 * changement de rôle (ActiveRoleService), les liens Recette / Présentation de
 * l'administrateur d'organisme et la déconnexion.
 */
@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, ButtonModule, InputText, IconField, InputIcon, MenuModule, TooltipModule],
    template: `
        <div class="layout-topbar">
            <button
                #menubutton
                class="topbar-menubutton"
                type="button"
                (click)="onMenuButtonClick()"
                [attr.aria-label]="menuOpen() ? 'Masquer le menu' : 'Afficher le menu'"
                [attr.aria-expanded]="menuOpen()"
                aria-controls="layout-menu-principal"
            >
                <span aria-hidden="true"></span>
            </button>

            <a class="app-logo" [routerLink]="$any(configService).authenticatedHomePath" [attr.aria-label]="appName() + ', tableau de bord'">
                <img [src]="layoutService.layoutConfig().logoUrl" alt="" class="app-logo-img" />
                <span class="app-title">
                    <span class="app-name">{{ appName() }}</span>
                    @if (layoutService.layoutConfig().appDescription; as description) {
                        <span class="app-description">{{ description }}</span>
                    }
                </span>
            </a>

            <ul class="topbar-menu" aria-label="Onglets ouverts">
                <li *ngFor="let item of tabs; let i = index">
                    <a
                        [routerLink]="item.routerLink"
                        routerLinkActive="active-route"
                        [routerLinkActiveOptions]="item.routerLinkActiveOptions || { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
                        [fragment]="item.fragment"
                        [queryParamsHandling]="item.queryParamsHandling"
                        [preserveFragment]="item.preserveFragment!"
                        [skipLocationChange]="item.skipLocationChange!"
                        [replaceUrl]="item.replaceUrl!"
                        [state]="item.state"
                        [queryParams]="item.queryParams"
                    >
                        <span>{{ item.label }}</span>
                    </a>
                    <button type="button" class="topbar-tab-close" [attr.aria-label]="'Fermer l’onglet ' + item.label" (click)="removeTab($event, item, i)">
                        <i class="pi pi-times" aria-hidden="true"></i>
                    </button>
                </li>
            </ul>

            <div class="topbar-actions">
                <p-button *ngIf="layoutService.layoutConfig().showSettings === true" icon="pi pi-palette" [rounded]="true" ariaLabel="Configurer l’apparence" (onClick)="layoutService.showConfigSidebar()"></p-button>

                <div *ngIf="layoutService.layoutConfig().showSearch === true" class="topbar-search" [ngClass]="{ 'topbar-search-active': searchActive }">
                    <button pButton [rounded]="true" severity="secondary" type="button" icon="pi pi-search" aria-label="Rechercher" (click)="activateSearch()"></button>
                    <div class="search-input-wrapper">
                        <p-icon-field>
                            <input #searchinput type="text" pInputText placeholder="Rechercher" aria-label="Rechercher" (blur)="deactivateSearch()" (keydown.escape)="deactivateSearch()" />
                            <p-inputicon class="pi pi-search" />
                        </p-icon-field>
                    </div>
                </div>

                <button
                    type="button"
                    class="topbar-theme-button"
                    data-testid="bascule-theme"
                    (click)="toggleTheme()"
                    [attr.aria-pressed]="layoutService.isDarkTheme()"
                    [attr.aria-label]="layoutService.isDarkTheme() ? 'Passer en apparence claire' : 'Passer en apparence sombre'"
                    [pTooltip]="layoutService.isDarkTheme() ? 'Apparence claire' : 'Apparence sombre'"
                    tooltipPosition="bottom"
                >
                    <i class="pi" [ngClass]="layoutService.isDarkTheme() ? 'pi-sun' : 'pi-moon'" aria-hidden="true"></i>
                </button>

                <button
                    type="button"
                    class="topbar-profile-button"
                    data-testid="profil"
                    aria-haspopup="menu"
                    aria-controls="menu-profil"
                    [attr.aria-expanded]="profileOpen()"
                    (click)="profileMenu.toggle($event)"
                >
                    @if (layoutService.layoutState().userAvatarUrl) {
                        <img alt="" class="profile-avatar" [src]="layoutService.layoutState().userAvatarUrl" />
                    } @else {
                        <span class="profile-avatar profile-initials" aria-hidden="true">{{ userInitials() }}</span>
                    }
                    <span class="profile-details">
                        <span class="profile-name">{{ userName() }}</span>
                        <span class="profile-job">{{ activeRoleService.activeRoleLabel() }}</span>
                    </span>
                    <i class="pi pi-angle-down profile-chevron" aria-hidden="true"></i>
                    <span class="sr-only">Ouvrir le menu du profil</span>
                </button>
                <p-menu
                    #profileMenu
                    id="menu-profil"
                    [model]="profileItems()"
                    [popup]="true"
                    appendTo="body"
                    styleClass="topbar-profile-menu"
                    ariaLabel="Menu du profil"
                    (onShow)="profileOpen.set(true)"
                    (onHide)="profileOpen.set(false)"
                >
                    <ng-template #start>
                        <div class="profile-menu-head">
                            <span class="profile-avatar profile-initials" aria-hidden="true">{{ userInitials() }}</span>
                            <span class="profile-menu-identity">
                                <span class="profile-name">{{ userName() }}</span>
                                @if (userEmail(); as email) {
                                    <span class="profile-mail">{{ email }}</span>
                                }
                            </span>
                        </div>
                    </ng-template>
                </p-menu>
            </div>
        </div>
    `,
})
export class AppTopbar {
    @ViewChild('searchinput') searchInput!: ElementRef;

    @ViewChild('menubutton') menuButton!: ElementRef;

    @ViewChild('profileMenu') profileMenu!: Menu;

    searchActive = false;

    layoutService = inject(LayoutService);
    userService = inject(UserService);
    configService = inject(ApplicationConfigService);
    authService = inject(AuthService);
    activeRoleService = inject(ActiveRoleService);

    /** Utilisateur courant (signal du Cadriciel). */
    currentUserSignal = this.userService.currentUser;

    /** Vrai quand le menu de profil est ouvert (aria-expanded). */
    readonly profileOpen = signal(false);

    readonly appName = computed(() => this.layoutService.layoutConfig().appName || 'Abonéo');

    /** Menu latéral visible (bureau : statique non masqué ; mobile : panneau ouvert). */
    readonly menuOpen = computed(() => {
        const state = this.layoutService.layoutState();
        return this.layoutService.isDesktop() ? !state.staticMenuDesktopInactive : state.staticMenuMobileActive;
    });

    readonly userName = computed(() => {
        const user = this.currentUserSignal();
        if (!user) return '';
        return [user.prenom, user.nom].filter(Boolean).join(' ') || user.email;
    });

    readonly userEmail = computed(() => (this.currentUserSignal() as { email?: string } | null)?.email ?? '');

    readonly userInitials = computed(() => {
        const user = this.currentUserSignal();
        if (!user) return '';
        const fp = user.prenom ? user.prenom.charAt(0).toUpperCase() : '';
        const fn = user.nom ? user.nom.charAt(0).toUpperCase() : '';
        return `${fp}${fn}` || (user.email ?? '?').charAt(0).toUpperCase();
    });

    /**
     * Modèle du menu de profil. `computed` : référence stable entre deux
     * détections de changement, recalculée seulement au changement de rôle.
     */
    readonly profileItems = computed<MenuItem[]>(() => {
        const roles = this.activeRoleService.availableRoles();
        const active = this.activeRoleService.activeRole();
        const items: MenuItem[] = [];

        if (roles.length > 1) {
            items.push({
                label: 'Changer de rôle',
                items: roles.map((role) => ({
                    label: this.activeRoleService.roleLabel(role),
                    icon: role === active ? 'pi pi-check-circle' : 'pi pi-circle',
                    styleClass: role === active ? 'profile-role-active' : undefined,
                    command: () => this.activeRoleService.setActiveRole(role),
                })),
            });
        }

        if (this.activeRoleService.isOrgAdminActive()) {
            items.push({
                label: 'Ressources',
                items: [
                    { label: 'Recette', icon: 'pi pi-clipboard', url: '/recette/', target: '_blank' },
                    { label: 'Présentation', icon: 'pi pi-desktop', url: '/presentation/', target: '_blank' },
                ],
            });
        }

        items.push({
            label: 'Session',
            items: [{ label: 'Se déconnecter', icon: 'pi pi-sign-out', command: () => this.authService.logout() }],
        });
        return items;
    });

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    toggleTheme() {
        this.layoutService.setDarkTheme(!this.layoutService.isDarkTheme());
    }

    activateSearch() {
        this.searchActive = true;
        setTimeout(() => {
            this.searchInput.nativeElement.focus();
        }, 100);
    }

    deactivateSearch() {
        this.searchActive = false;
    }

    removeTab(event: Event, item: MenuItem, index: number) {
        this.layoutService.onTabClose(item, index);
        event.preventDefault();
    }

    get tabs(): MenuItem[] {
        return this.layoutService.tabs;
    }
}
